import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { lookupApi } from 'services/lookup/lookup';
import { myAccountApi } from 'services/myAccount/myAccount';
import { programsApi } from 'services/programs/programs';
import { surveysApi } from 'services/surveys/surveys';

import { selectUser } from 'store';

import { useAppSelector } from 'hooks';

export interface BootstrapQueryStatus {
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isUninitialized: boolean;
}

export interface AuthBootstrapContextValue {
  isBootstrapped: boolean;
  membershipPlans: BootstrapQueryStatus;
  myAccount: BootstrapQueryStatus;
  myPrograms: BootstrapQueryStatus;
  surveys: BootstrapQueryStatus;
}

const defaultStatus: BootstrapQueryStatus = {
  isError: false,
  isFetching: false,
  isSuccess: false,
  isUninitialized: true
};

const defaultValue: AuthBootstrapContextValue = {
  isBootstrapped: false,
  membershipPlans: defaultStatus,
  myAccount: defaultStatus,
  myPrograms: defaultStatus,
  surveys: defaultStatus
};

export const AuthBootstrapContext = createContext<AuthBootstrapContextValue>(defaultValue);

export const useAuthBootstrapStatus = () => useContext(AuthBootstrapContext);

const toStatus = (state: {
  isError?: boolean;
  isFetching?: boolean;
  isSuccess?: boolean;
  isUninitialized?: boolean;
}): BootstrapQueryStatus => ({
  isError: !!state.isError,
  isFetching: !!state.isFetching,
  isSuccess: !!state.isSuccess,
  isUninitialized: state.isUninitialized ?? true
});

/**
 * A query is settled once it has been triggered and has come back, successfully
 * or not. An error counts: callers need to stop waiting and act on what they
 * have, rather than block forever on a fetch that will not arrive.
 */
export const isBootstrapQuerySettled = (s: BootstrapQueryStatus) =>
  !s.isUninitialized && !s.isFetching && (s.isSuccess || s.isError);

/**
 * Observes the post-auth fetches that other parts of the app (App.tsx's bootstrap
 * effect) trigger when accessToken becomes truthy, and exposes a single
 * `isBootstrapped` flag that consumers can wait on before navigating into
 * auth-gated routes. Surveys is intentionally excluded from the gate because it
 * is only fetched when the user has programs, so a brand-new user would never
 * fire it and the gate would stay closed.
 */
export const AuthBootstrapProvider = ({ children }: PropsWithChildren) => {
  const { accessToken } = useAppSelector(selectUser);

  const myAccountState = myAccountApi.endpoints.getMyAccount.useQueryState(undefined);
  const myProgramsState = programsApi.endpoints.getMyPrograms.useQueryState(undefined);
  const membershipPlansState = lookupApi.endpoints.getMembershipPlans.useQueryState(undefined);
  const surveysState = surveysApi.endpoints.getSurveys.useQueryState(undefined);

  const value = useMemo<AuthBootstrapContextValue>(() => {
    const myAccount = toStatus(myAccountState);
    const myPrograms = toStatus(myProgramsState);
    const membershipPlans = toStatus(membershipPlansState);
    const surveys = toStatus(surveysState);
    const isBootstrapped =
      !!accessToken &&
      isBootstrapQuerySettled(myAccount) &&
      isBootstrapQuerySettled(myPrograms) &&
      isBootstrapQuerySettled(membershipPlans);
    return { isBootstrapped, membershipPlans, myAccount, myPrograms, surveys };
  }, [accessToken, myAccountState, myProgramsState, membershipPlansState, surveysState]);

  return <AuthBootstrapContext.Provider value={value}>{children}</AuthBootstrapContext.Provider>;
};
